import { fetchEventSource } from "@microsoft/fetch-event-source";
import { NOTIFICATION_TYPES, type ConnectionStatus, type NotificationType } from "../types";
import type {
  ConnectionStatusHandler,
  GetTokenFn,
  NotificationHandler,
  NotificationsClient,
} from "./types";

/** Stream SSE do backend. Relativo: o proxy `/v1` do Vite (e o gateway em prod) cobre. */
const STREAM_PATH = "/v1/notifications/stream";
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;
/** Evento de keep-alive do servidor; ignorado. */
const KEEP_ALIVE_EVENT = "ping";

/**
 * Cliente de notificações em tempo real sobre Server-Sent Events
 * (`@microsoft/fetch-event-source`). Recebe-only: o backend emite um evento SSE
 * por tipo de notificação (`BrokerJoined`, `SupportReplied`, …) com o payload
 * JSON; ações de volta (marcar lida etc.) continuam via REST. Mesma interface
 * `NotificationsClient` do crm/launches, então UI, hook e store não mudam.
 */
class SseClient implements NotificationsClient {
  private controller: AbortController | null = null;
  private getToken: GetTokenFn = () => null;
  private status: ConnectionStatus = "disconnected";
  private statusHandlers: ConnectionStatusHandler[] = [];
  /** Mapa de evento → handlers, preservado entre reconstruções. */
  private handlers = new Map<NotificationType, NotificationHandler<NotificationType>[]>();
  /** Header mutável: relido a cada (re)conexão para refletir refresh de token. */
  private headers: Record<string, string> = {};
  private reconnectMs = RECONNECT_BASE_MS;

  build(getToken: GetTokenFn): void {
    void this.stop();
    this.getToken = getToken;
  }

  async start(): Promise<void> {
    if (this.controller) return;

    const controller = new AbortController();
    this.controller = controller;
    this.reconnectMs = RECONNECT_BASE_MS;
    this.emitStatus("reconnecting");

    let opened = false;
    await new Promise<void>((resolve, reject) => {
      this.refreshAuth();
      void fetchEventSource(STREAM_PATH, {
        signal: controller.signal,
        headers: this.headers,
        openWhenHidden: true,
        onopen: async (response) => {
          const contentType = response.headers.get("content-type") ?? "";
          if (response.ok && contentType.includes("text/event-stream")) {
            opened = true;
            this.reconnectMs = RECONNECT_BASE_MS;
            this.emitStatus("connected");
            resolve();
            return;
          }
          // Resposta inesperada (401/500/HTML): erro fatal, não reconecta.
          throw new Error(`SSE open failed: ${response.status}`);
        },
        onmessage: (ev) => this.dispatch(ev.event, ev.data),
        onerror: (err) => {
          this.emitStatus("reconnecting");
          if (!opened) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
          // Renova o token e aplica backoff exponencial limitado.
          this.refreshAuth();
          const delay = this.reconnectMs;
          this.reconnectMs = Math.min(this.reconnectMs * 2, RECONNECT_MAX_MS);
          return delay;
        },
        onclose: () => {
          // Servidor encerrou o stream: sinaliza e deixa o fetch-event-source reabrir.
          this.emitStatus("reconnecting");
        },
      }).catch(() => {
        // Encerramento fatal (onopen lançou) ou abort.
        this.emitStatus("disconnected");
      });
    }).catch((err) => {
      this.emitStatus("disconnected");
      throw err;
    });
  }

  async stop(): Promise<void> {
    if (!this.controller) return;
    this.controller.abort();
    this.controller = null;
    this.emitStatus("disconnected");
    return Promise.resolve();
  }

  on<T extends NotificationType>(event: T, handler: NotificationHandler<T>): void {
    const list = this.handlers.get(event) ?? [];
    list.push(handler as unknown as NotificationHandler<NotificationType>);
    this.handlers.set(event, list);
  }

  off(event: NotificationType): void {
    this.handlers.delete(event);
  }

  getConnectionState(): ConnectionStatus {
    return this.status;
  }

  onStatusChange(handler: ConnectionStatusHandler): void {
    this.statusHandlers.push(handler);
  }

  private refreshAuth(): void {
    const token = this.getToken();
    this.headers.Authorization = token ? `Bearer ${token}` : "";
  }

  private dispatch(event: string | undefined, data: string): void {
    if (!event || event === KEEP_ALIVE_EVENT || !data) return;
    if (!NOTIFICATION_TYPES.includes(event as NotificationType)) return;

    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch (err) {
      console.error(`[sse] payload inválido para ${event}:`, err);
      return;
    }

    const list = this.handlers.get(event as NotificationType);
    if (!list) return;
    for (const handler of list) {
      try {
        (handler as (p: unknown) => void)(payload);
      } catch (err) {
        console.error("[sse] handler threw:", err);
      }
    }
  }

  private emitStatus(status: ConnectionStatus): void {
    this.status = status;
    for (const h of this.statusHandlers) {
      try {
        h(status);
      } catch (err) {
        console.error("[sse] status handler threw:", err);
      }
    }
  }
}

export const sseClient: NotificationsClient = new SseClient();
